/* eslint-disable max-lines-per-function, import/no-default-export, import/order, max-lines, no-nested-ternary, react/no-unescaped-entities, react/prop-types */
// @ts-expect-error TS(6133): 'React' is declared but its value is never read.
import React, { useCallback, useRef } from "react";
import { cn } from "tailwind-variants";
import FontPreviewOption from "./FontPreviewOption.vue";
import { injectHexColorPreviews } from "./composables/useMarkdownRenderer";
import { renderList } from "common/vue/render-list";

export default function PinClarifications({
  clarifications,
  responses,
  isDisabled = false,
  textInfo = undefined,
  selectedFont = undefined,
  selectedColor = undefined,
  unansweredAbove = 0,
  unansweredBelow = 0,
  compact = false,
  onSelectOption,
  onToggleCustomInput,
  onCustomInputChange,
  onSubmit,
  onScroll,
  onScrollToUnanswered,
  ...restProps
}: any) {
  const CLARIFICATION_CONTAINER_MAX_HEIGHT = 280;
  const DEFAULT_RESPONSE = {
    selected: null,
    custom: "",
    isTypingCustom: false
  };

  const scrollContainerRef = useRef(null);
  const prevCustomInputElRef = useRef<any>(null);

  function answeredCount() {
    return clarifications.filter((c: any) => {
      const r = responses[c.question];
      if (!r) return false;
      if (r.isTypingCustom) return !!r.custom?.trim();
      return !!r.selected;
    }).length;
  }

  function effectiveFont() {
    return selectedFont || textInfo?.fontFamily || "inherit";
  }

  function effectiveColor() {
    return selectedColor || textInfo?.color || "#000000";
  }

  function getResponse(question: any) {
    return responses[question] ?? DEFAULT_RESPONSE;
  }

  function isFontPreview(clarification: any) {
    return clarification.type === "font_selection" || clarification.type === "font_color";
  }

  function getTextValue(clarification: any) {
    return textInfo?.value || clarification.label || "Sample";
  }

  function getDisplayText(clarification: any, opt: any) {
    const isFontColor = clarification.type === "font_color";
    const isColorShade = clarification.type === "color_shade";
    return (isColorShade || isFontColor) && opt.value ? `${opt.label} (${opt.value})` : opt.label;
  }

  const autoFocusInput = useCallback((el: any) => {
    if (el && el !== prevCustomInputElRef.current) {
      prevCustomInputElRef.current = el;
      queueMicrotask(() => el.focus());
    }
  }, []);

  function handleOptionSelect(question: any, label: any) {
    const response = getResponse(question);
    const isSelected = !response.isTypingCustom && response.selected === label;
    onSelectOption?.(question, isSelected ? null : label);
  }

  return (
    <div
      {...restProps}
      className={cn(
        "rounded-lg border border-gray-200 bg-gray-50",
        compact ? "mb-2 p-1.5" : "mb-4 p-3"
      )}
    >
      <div
        className={cn(
          "flex items-center justify-between text-gray-500",
          compact ? "mb-1 text-[10px]" : "mb-2 text-xs"
        )}
      >
        <span>
          {clarifications.length} question{clarifications.length > 1 ? "s" : ""} to answer
        </span>
        <span>
          {answeredCount()} of {clarifications.length} answered
        </span>
      </div>
      <div className="relative">
        <div
          className={cn("flex flex-col overflow-y-auto pr-1", compact ? "gap-2" : "gap-4")}
          ref={scrollContainerRef}
          style={{
            maxHeight: `${CLARIFICATION_CONTAINER_MAX_HEIGHT}px`
          }}
          onScroll={() => {
            onScroll?.();
          }}
          data-testid="flex"
        >
          {renderList(clarifications, clarification => (
            <div
              className={cn("flex flex-col", compact ? "gap-1.5" : "gap-2")}
              key={clarification.question}
              data-clarification-label={clarification.label || clarification.question}
            >
              <div
                className="text-sm text-gray-800"
                dangerouslySetInnerHTML={{
                  __html: injectHexColorPreviews(clarification.question)
                }}
              />
              <div className={cn("flex flex-wrap", compact ? "gap-1.5" : "gap-2")}>
                {renderList(clarification.options, opt => (
                  <>
                    {isFontPreview(clarification) ? (
                      <FontPreviewOption
                        textValue={getTextValue(clarification)}
                        fontFamily={
                          clarification.type === "font_selection"
                            ? opt.value || opt.label
                            : effectiveFont()
                        }
                        color={
                          clarification.type === "font_color"
                            ? opt.value || opt.label
                            : effectiveColor()
                        }
                        optionLabel={opt.label}
                        optionValue={opt.value || opt.label}
                        isSelected={
                          !getResponse(clarification.question).isTypingCustom &&
                          getResponse(clarification.question).selected ===
                            getDisplayText(clarification, opt)
                        }
                        isDisabled={isDisabled}
                        previewType={clarification.type === "font_selection" ? "font" : "color"}
                        compact={compact}
                        onSelect={() => {
                          handleOptionSelect(
                            clarification.question,
                            getDisplayText(clarification, opt)
                          );
                        }}
                        data-testid="font-preview-option"
                      />
                    ) : (
                      <div
                        className={cn(
                          "cursor-pointer border font-medium",
                          compact
                            ? "rounded-sm px-2.5 py-1 text-xs"
                            : "rounded-lg px-4 py-2 text-sm",
                          !getResponse(clarification.question).isTypingCustom &&
                            getResponse(clarification.question).selected ===
                              getDisplayText(clarification, opt)
                            ? "border-blue-500 bg-blue-50 text-blue-700 hover:bg-blue-100"
                            : "border-gray-300 bg-white text-gray-700 hover:bg-gray-50",
                          isDisabled && "cursor-not-allowed opacity-50"
                        )}
                        role="button"
                        tabIndex={isDisabled ? -1 : 0}
                        data-testid={`pin-clarify-${opt.label}`}
                        onClick={() => {
                          !isDisabled &&
                            handleOptionSelect(
                              clarification.question,
                              getDisplayText(clarification, opt)
                            );
                        }}
                        onKeyDown={e => {
                          if (e.key !== "Enter" && e.key !== " ") return;
                          e.preventDefault();
                          if (!isDisabled) {
                            handleOptionSelect(
                              clarification.question,
                              getDisplayText(clarification, opt)
                            );
                          }
                        }}
                      >
                        <span
                          dangerouslySetInnerHTML={{
                            __html: injectHexColorPreviews(getDisplayText(clarification, opt))
                          }}
                        />
                      </div>
                    )}
                  </>
                ))}
              </div>
              {getResponse(clarification.question).isTypingCustom ? (
                <div className="flex items-center gap-2">
                  <input
                    className="mp-unmask flex-1 rounded-md border border-gray-300 px-2.5 py-1.5 outline-none focus:border-blue-500 disabled:opacity-50"
                    ref={autoFocusInput}
                    type="text"
                    value={getResponse(clarification.question).custom}
                    placeholder="Type your answer..."
                    style={{ fontSize: "16px" }}
                    disabled={isDisabled}
                    data-testid={`pin-clarify-custom-input-${clarification.type}`}
                    onInput={e => {
                      onCustomInputChange?.(
                        clarification.question,
                        (e.target as HTMLInputElement).value
                      );
                    }}
                    onKeyDown={e => {
                      if (e.key !== "Enter") return;
                      e.preventDefault();
                      if (!isDisabled) onSubmit?.();
                    }}
                  />
                  <button
                    className="cursor-pointer border-none bg-transparent text-[13px] text-gray-500 hover:text-gray-700 disabled:opacity-50"
                    type="button"
                    disabled={isDisabled}
                    data-testid={`pin-clarify-cancel-custom-${clarification.type}`}
                    onClick={() => {
                      onToggleCustomInput?.(clarification.question);
                    }}
                  >
                    {" "}
                    Cancel{" "}
                  </button>
                </div>
              ) : (
                <div
                  className={cn(
                    "cursor-pointer self-start border border-dashed border-gray-300 text-blue-600 hover:border-blue-400 hover:bg-blue-50 hover:text-blue-700",
                    compact ? "rounded-sm px-2.5 py-1 text-xs" : "rounded-lg px-4 py-2 text-sm",
                    isDisabled && "cursor-not-allowed opacity-50"
                  )}
                  role="button"
                  tabIndex={isDisabled ? -1 : 0}
                  data-testid={`pin-clarify-type-own-${clarification.type}`}
                  onClick={() => {
                    !isDisabled && onToggleCustomInput?.(clarification.question);
                  }}
                  onKeyDown={e => {
                    if (e.key !== "Enter" && e.key !== " ") return;
                    e.preventDefault();
                    if (!isDisabled) onToggleCustomInput?.(clarification.question);
                  }}
                >
                  {" "}
                  + Type my own{" "}
                </div>
              )}
            </div>
          ))}
        </div>
        {unansweredAbove > 0 ? (
          <div
            className={cn(
              "absolute left-1/2 top-[-4px] z-10 -translate-x-1/2 cursor-pointer rounded-full bg-blue-500 font-medium text-white shadow-md hover:bg-blue-600",
              compact ? "px-2 py-0.5 text-[10px]" : "px-3 py-1 text-xs"
            )}
            role="button"
            tabIndex={0}
            aria-label={`Scroll to ${unansweredAbove} unanswered above`}
            onClick={() => {
              onScrollToUnanswered?.("above");
            }}
            onKeyDown={e => {
              if (e.key !== "Enter" && e.key !== " ") return;
              e.preventDefault();
              onScrollToUnanswered?.("above");
            }}
            data-testid="absolute"
          >
            {" "}
            ↑ {unansweredAbove} unanswered above{" "}
          </div>
        ) : null}
        {unansweredBelow > 0 ? (
          <div
            className={cn(
              "absolute bottom-[-4px] left-1/2 z-10 -translate-x-1/2 cursor-pointer rounded-full bg-blue-500 font-medium text-white shadow-md hover:bg-blue-600",
              compact ? "px-2 py-0.5 text-[10px]" : "px-3 py-1 text-xs"
            )}
            role="button"
            tabIndex={0}
            aria-label={`Scroll to ${unansweredBelow} unanswered below`}
            onClick={() => {
              onScrollToUnanswered?.("below");
            }}
            onKeyDown={e => {
              if (e.key !== "Enter" && e.key !== " ") return;
              e.preventDefault();
              onScrollToUnanswered?.("below");
            }}
            data-testid="absolute-1"
          >
            {unansweredBelow} unanswered below ↓{" "}
          </div>
        ) : null}
      </div>
    </div>
  );
}
